import { CheckCircle2, Crown, KeyRound } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { DashboardUserProfile } from '@services/firebase'
import {
  deriveLearnerAccessStatus,
  formatLearnerAccessDate,
} from '@features/premium/shared/learner-access-status'
import '../profile-layout.css'

export function ProfileMembershipHero({
  profile,
}: {
  readonly profile: DashboardUserProfile
}) {
  const { i18n, t } = useTranslation('translation')
  const accessStatus = deriveLearnerAccessStatus({ locale: i18n.language, profile, t })
  const activationKey = accessStatus.activationKey
  const shouldShowCountdown =
    accessStatus.progressPercent !== null &&
    accessStatus.hasActivationRecord &&
    (
      accessStatus.state === 'ACTIVE' ||
      accessStatus.state === 'EXPIRING_SOON' ||
      accessStatus.state === 'EXPIRED' ||
      accessStatus.state === 'TRIAL'
    )
  const StatusIcon =
    accessStatus.state === 'ACTIVE'
      ? Crown
      : accessStatus.state === 'TRIAL'
        ? CheckCircle2
        : KeyRound
  const expiryLabel = activationKey?.endAt
    ? formatLearnerAccessDate(activationKey.endAt, i18n.language, t)
    : null
  const statusMeta = [accessStatus.remainingLabel, expiryLabel]
    .filter((value): value is string => Boolean(value))
    .join(' · ')

  return (
    <div className="profileMembershipHero">
      <div className="profileMembershipHeroRow">
        <span className={`profileSubscriptionBadge is-${accessStatus.tone}`}>
          <StatusIcon aria-hidden="true" className="profileSubscriptionIcon" />
          {accessStatus.label}
        </span>
        <p className="profileSubscriptionStatusTitle">
          {accessStatus.statusTitle}
        </p>
        {statusMeta ? (
          <p className="profileSubscriptionStatusMeta">{statusMeta}</p>
        ) : null}
      </div>

      {shouldShowCountdown ? (
        <div className="profileMembershipHeroCountdown">
          <div
            aria-label={t('{{percent}}% of this plan has elapsed', {
              defaultValue: '{{percent}}% of this plan has elapsed',
              percent: accessStatus.progressPercent ?? 0,
            })}
            aria-valuemax={100}
            aria-valuemin={0}
            aria-valuenow={accessStatus.progressPercent ?? 0}
            className="profileSubscriptionProgressTrack"
            role="progressbar"
          >
            <span
              className="profileSubscriptionProgressValue"
              style={{ width: `${accessStatus.progressPercent ?? 0}%` }}
            />
          </div>
          <p className="profileSubscriptionCountdownMeta">
            {t('{{percent}}% elapsed since activation.', {
              defaultValue: '{{percent}}% elapsed since activation.',
              percent: accessStatus.progressPercent ?? 0,
            })}
          </p>
        </div>
      ) : null}
    </div>
  )
}
